import React from "react";
import PropTypes from "prop-types";
import { Button } from "@mui/material";
import { styled } from "@mui/material/styles";

const StyledButton = styled(Button, {
  name: "EMPButton",
  slot: "root",
})(({ theme }) => ({
  borderRadius: 24,
  fontSize: theme.typography.pxToRem(14),
  transition: "none",
}));

const getButtonComponent = (buttonElement, href) => {
  if (buttonElement) return buttonElement;
  if (href) return "a";
  return "button";
};

const SHARED_BASE = {
  height: "40px",
  padding: "8px 12px",
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: "20px",
  textTransform: "none",
};

const makeContained = (bgColor, hoverColor, border = "none") => ({
  backgroundColor: bgColor,
  color: "#FFFFFF",
  border,
  "&:hover": { backgroundColor: hoverColor },
});

const makeOutlined = (color, hoverBorderColor) => ({
  backgroundColor: "transparent",
  color,
  border: `1px solid ${color}`,
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    borderColor: hoverBorderColor,
  },
});

const makeText = (color) => ({
  backgroundColor: "transparent",
  color,
  border: "none",
});

const BRAND_STYLES = {
  ford: {
    base: { ...SHARED_BASE, minWidth: "221px", borderRadius: "400px" },
    contained: makeContained("#0562D2", "#044FA8", "1px solid #0562D2"),
    outlined: makeOutlined("#0562D2", "#044FA8"),
    text: makeText("#0562D2"),
  },
  default: {
    base: { ...SHARED_BASE, minWidth: "243px", borderRadius: "4px" },
    contained: makeContained("#485B65", "#3A4A53"),
    outlined: makeOutlined("#485B65", "#3A4A53"),
    text: makeText("#485B65"),
  },
};

const getBrandStyles = (isFord, variant) => {
  if (isFord === undefined) return undefined;

  const brand = isFord ? BRAND_STYLES.ford : BRAND_STYLES.default;
  return { ...brand.base, ...(brand[variant] || undefined) };
};

const getButtonType = (submitButton) => (submitButton ? "submit" : "button");

const VALID_BUTTON_COLORS = new Set([
  "primary",
  "secondary",
  "success",
  "error",
  "info",
  "warning",
  "inherit",
]);

const getSafeButtonColor = (color) =>
  color && VALID_BUTTON_COLORS.has(color) ? color : "primary";

const EMPButton = React.forwardRef((props, ref) => {
  const {
    testId,
    disabled,
    children,
    variant,
    color,
    rightIcon,
    leftIcon,
    onClick,
    submitButton,
    id,
    href,
    buttonElement,
    link,
    target,
    rel,
    sx,
    isFord,
    isFordBrand,
    ...otherProps
  } = props;

  const Component = getButtonComponent(buttonElement, href);
  const brandStyles = getBrandStyles(isFordBrand ?? isFord, variant);
  const mergedSx = sx ? { ...brandStyles, ...sx } : brandStyles;
  const safeColor = getSafeButtonColor(color);

  return (
    <StyledButton
      component={Component}
      disabled={disabled}
      variant={variant}
      startIcon={leftIcon}
      endIcon={rightIcon}
      onClick={onClick}
      type={getButtonType(submitButton)}
      id={id}
      ref={ref}
      data-testid={testId}
      href={href}
      to={link}
      target={target}
      rel={rel}
      sx={mergedSx}
      {...otherProps}
      color={safeColor}
    >
      {children}
    </StyledButton>
  );
});

EMPButton.propTypes = {
  testId: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  variant: PropTypes.oneOf(["contained", "outlined", "text"]),
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "error",
    "info",
    "warning",
    "inherit",
  ]),
  rightIcon: PropTypes.node,
  leftIcon: PropTypes.node,
  onClick: PropTypes.func,
  submitButton: PropTypes.bool,
  id: PropTypes.string,
  href: PropTypes.string,
  buttonElement: PropTypes.elementType,
  link: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  sx: PropTypes.object,
  isFord: PropTypes.bool,
  isFordBrand: PropTypes.bool,
};

EMPButton.defaultProps = {
  testId: undefined,
  disabled: false,
  children: null,
  variant: "contained",
  color: "primary",
  rightIcon: null,
  leftIcon: null,
  onClick: undefined,
  submitButton: false,
  id: undefined,
  href: undefined,
  buttonElement: undefined,
  link: undefined,
  target: undefined,
  rel: undefined,
  sx: undefined,
  isFord: undefined,
  isFordBrand: undefined,
};

EMPButton.displayName = "EMPButton";

export default EMPButton;
